import {useAppDataStore} from "@/mobx/appDataStore.jsx";
import {DataTable} from "components/dataTable/index.jsx";
import {useEffect, useState, useMemo, useCallback} from "react";
import {observer} from "mobx-react";
import moment from "moment";
import formatters from "helpers/formatters.jsx";


const StatusBadge = ({status}) => {
  const colors = {
    available: "bg-green-100 text-green-800",
    used: "bg-blue-100 text-blue-800",
    expired: "bg-red-100 text-red-800",
    error: "bg-gray-100 text-gray-800",
  };
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${colors[status] || "bg-gray-100 text-gray-800"}`}>
      {status}
    </span>
  );
};

const NuplinStateBadge = ({state}) => {
  if (!state) return <span className="text-gray-400 text-xs">—</span>;
  const colors = {
    COMPLETED: "bg-green-100 text-green-800",
    CANCELED_SCHEDULED: "bg-red-100 text-red-800",
    DOWNGRADE_SCHEDULE: "bg-yellow-100 text-yellow-800",
    UPGRADE_SCHEDULE: "bg-blue-100 text-blue-800",
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[state] || "bg-gray-100 text-gray-700"}`}>
      {state}
    </span>
  );
};

export default observer(function AdminNuplinTokens() {
  const appDataStore = useAppDataStore();
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [selectedTokenIds, setSelectedTokenIds] = useState([]);

  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({costPrice: "", salePrice: ""});

  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [bulkNewStatus, setBulkNewStatus] = useState("");

  const [showNuplinModal, setShowNuplinModal] = useState(null);
  const [nuplinToken, setNuplinToken] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState("");
  const [changeTierForm, setChangeTierForm] = useState({product: "", policy: "IMMEDIATE"});
  const [initiatePurchaseForm, setInitiatePurchaseForm] = useState({sku: "", months: 1});

  const [webhookEventsToken, setWebhookEventsToken] = useState(null);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [loadingWebhookEvents, setLoadingWebhookEvents] = useState(false);
  const [expandedPayload, setExpandedPayload] = useState(null);

  const [showBulkDeactivateModal, setShowBulkDeactivateModal] = useState(false);
  const [bulkDeactivateInput, setBulkDeactivateInput] = useState("");
  const [bulkDeactivateReason, setBulkDeactivateReason] = useState("");
  const [bulkDeactivatePreview, setBulkDeactivatePreview] = useState(null);
  const [bulkDeactivateResults, setBulkDeactivateResults] = useState(null);

  const [showBulkChangeTierModal, setShowBulkChangeTierModal] = useState(false);
  const [bulkChangeTierInput, setBulkChangeTierInput] = useState("");
  const [bulkChangeTierForm, setBulkChangeTierForm] = useState({toProduct: "", policy: "IMMEDIATE"});
  const [bulkChangeTierPreview, setBulkChangeTierPreview] = useState(null);
  const [bulkChangeTierResults, setBulkChangeTierResults] = useState(null);

  const [nuplinProducts, setNuplinProducts] = useState([]);

  useEffect(() => {
    appDataStore.get("/nuplin-tokens/skus")
      .then(res => {
        const skus = res.skus
        if (Array.isArray(skus)) {
          setNuplinProducts(skus.map(s => ({
            value: s.api_name,
            label: `${s.traduccion_paquete || s.name} (${s.tipo})`,
          })))
        }
      })
      .catch(err => console.error("Error cargando SKUs:", err))
  }, [])

  const userScopes = appDataStore.user?.scopes || [];
  const hasMasterScope = Array.isArray(userScopes) && userScopes.includes("master");

  // ---------- Handlers ----------

  const handleEditToken = (token) => {
    setSelectedToken(token);
    setEditForm({costPrice: token.costPrice || "", salePrice: token.salePrice || ""});
    setShowEditModal(true);
  };

  const submitEditToken = async () => {
    try {
      if (!editForm.costPrice && !editForm.salePrice) return alert("Ingresa al menos un precio");
      setLoading(true);
      await appDataStore.post("/nuplin-tokens/update", {
        id: selectedToken.id,
        costPrice: editForm.costPrice ? parseFloat(editForm.costPrice) : undefined,
        salePrice: editForm.salePrice ? parseFloat(editForm.salePrice) : undefined,
      });
      alert("Token actualizado");
      setShowEditModal(false);
      setRefreshKey(k => k + 1);
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStatus = async (token, newStatus) => {
    if (!confirm(`¿Cambiar estado a "${newStatus}"?`)) return;
    try {
      setLoading(true);
      await appDataStore.post("/nuplin-tokens/change-status", {id: token.id, status: newStatus});
      setRefreshKey(k => k + 1);
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitBulkStatusChange = async () => {
    if (!bulkNewStatus) return alert("Selecciona un estado");
    if (!selectedTokenIds.length) return alert("Selecciona al menos un token");
    if (!confirm(`¿Cambiar estado a "${bulkNewStatus}" para ${selectedTokenIds.length} tokens?`)) return;
    try {
      setLoading(true);
      const res = await appDataStore.post("/nuplin-tokens/bulk-status", {ids: selectedTokenIds, status: bulkNewStatus});
      alert(`${res.updated} tokens actualizados`);
      setShowBulkStatusModal(false);
      setBulkNewStatus("");
      setSelectedTokenIds([]);
      setRefreshKey(k => k + 1);
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const openNuplinModal = (type, token = null) => {
    setNuplinToken(token);
    setDeactivateReason("");
    setChangeTierForm({product: "", policy: "IMMEDIATE"});
    setInitiatePurchaseForm({sku: "", months: 1});
    setShowNuplinModal(type);
  };

  const closeNuplinModal = () => {
    setShowNuplinModal(null);
    setNuplinToken(null);
  };

  const submitInitiatePurchase = async () => {
    if (!initiatePurchaseForm.sku) return alert("Selecciona un SKU");
    try {
      setLoading(true);
      const res = await appDataStore.post("/nuplin-tokens/initiate-purchase", initiatePurchaseForm);
      alert(`Compra iniciada\nSubject ID: ${res.token?.subjectId || "—"}`);
      closeNuplinModal();
      setRefreshKey(k => k + 1);
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitDeactivate = async () => {
    if (!deactivateReason) return alert("Ingresa el motivo");
    if (!confirm(`¿Desactivar entitlement ${nuplinToken.entitlementId}?`)) return;
    try {
      setLoading(true);
      await appDataStore.post("/nuplin-tokens/deactivate", {id: nuplinToken.id, reason: deactivateReason});
      alert("Desactivación programada");
      closeNuplinModal();
      setRefreshKey(k => k + 1);
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitChangeTier = async () => {
    if (!changeTierForm.product) return alert("Selecciona el producto destino");
    if (changeTierForm.product === nuplinToken.product) return alert("El producto destino debe ser diferente");
    if (!confirm(`¿Cambiar tier de "${nuplinToken.product}" a "${changeTierForm.product}"?`)) return;
    try {
      setLoading(true);
      await appDataStore.post("/nuplin-tokens/change-tier", {id: nuplinToken.id, ...changeTierForm});
      alert("Cambio de tier completado");
      closeNuplinModal();
      setRefreshKey(k => k + 1);
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitResubscribe = async () => {
    if (!confirm(`¿Resuscribir entitlement ${nuplinToken.entitlementId}?`)) return;
    try {
      setLoading(true);
      await appDataStore.post("/nuplin-tokens/resubscribe", {id: nuplinToken.id});
      alert("Resuscripción completada");
      closeNuplinModal();
      setRefreshKey(k => k + 1);
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const openWebhookEvents = async (token) => {
    setWebhookEventsToken(token);
    setWebhookEvents([]);
    setExpandedPayload(null);
    try {
      setLoadingWebhookEvents(true);
      const res = await appDataStore.post("/nuplin-tokens/webhook-events", {nuplinAppTokenId: token.id});
      setWebhookEvents(res.events || []);
    } catch (err) {
      alert("Error al cargar eventos: " + err.message);
    } finally {
      setLoadingWebhookEvents(false);
    }
  };

  // Bulk deactivate
  const parsedEntitlementIds = bulkDeactivateInput.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);

  const fetchBulkDeactivatePreview = async () => {
    if (!parsedEntitlementIds.length) return alert("Ingresa al menos un Entitlement ID");
    try {
      setLoading(true);
      const res = await appDataStore.post("/nuplin-tokens/bulk-deactivate-preview", {entitlementIds: parsedEntitlementIds});
      setBulkDeactivatePreview(res.rows);
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitBulkDeactivate = async () => {
    if (!bulkDeactivateReason) return alert("Ingresa el motivo");
    if (!confirm(`¿Cancelar ${parsedEntitlementIds.length} entitlements?`)) return;
    try {
      setLoading(true);
      const res = await appDataStore.post("/nuplin-tokens/bulk-deactivate", {
        entitlementIds: parsedEntitlementIds,
        reason: bulkDeactivateReason,
      });
      setBulkDeactivateResults(res);
      setBulkDeactivatePreview(null);
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Bulk change tier
  const parsedChangeTierIds = bulkChangeTierInput.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);

  const fetchBulkChangeTierPreview = async () => {
    if (!parsedChangeTierIds.length) return alert("Ingresa al menos un Entitlement ID");
    try {
      setLoading(true);
      const res = await appDataStore.post("/nuplin-tokens/bulk-change-tier-preview", {entitlementIds: parsedChangeTierIds});
      setBulkChangeTierPreview(res.rows);
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitBulkChangeTier = async () => {
    if (!bulkChangeTierForm.toProduct) return alert("Selecciona el producto destino");
    if (!confirm(`¿Cambiar tier a "${bulkChangeTierForm.toProduct}" para ${parsedChangeTierIds.length} entitlements?`)) return;
    try {
      setLoading(true);
      const res = await appDataStore.post("/nuplin-tokens/bulk-change-tier", {
        entitlementIds: parsedChangeTierIds,
        toProduct: bulkChangeTierForm.toProduct,
        policy: bulkChangeTierForm.policy,
      });
      setBulkChangeTierResults(res);
      setBulkChangeTierPreview(null);
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Table ----------

  const handleSelectToken = (id, isSelected) =>
    setSelectedTokenIds(prev => isSelected ? [...prev, id] : prev.filter(x => x !== id));

  const handleSelectAll = (table) => {
    const all = table.getRowModel().rows.map(r => r.original.id);
    setSelectedTokenIds(prev => prev.length === all.length ? [] : all);
  };

  const columns = useMemo(() => [
    {
      id: "select",
      header: ({table}) => (
        <input type="checkbox" checked={table.getIsAllRowsSelected()}
          onChange={() => handleSelectAll(table)} className="w-4 h-4 rounded border-gray-300 cursor-pointer"/>
      ),
      cell: ({row}) => (
        <input type="checkbox" checked={selectedTokenIds.includes(row.original.id)}
          onChange={e => handleSelectToken(row.original.id, e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 cursor-pointer"/>
      ),
      enableColumnFilter: false,
      size: 40,
    },
    {accessorKey: "id", header: "ID"},
    {accessorKey: "subjectId", header: "Subject ID"},
    {accessorKey: "entitlementId", header: "Entitlement ID"},
    {accessorKey: "product", header: "Producto", opFilter: "eq"},
    {
      accessorKey: "validity", header: "Vigencia",
      cell: ({cell}) => { const v = cell.getValue(); return v ? `${v} ${v === 1 ? "mes" : "meses"}` : "—"; },
    },
    {
      accessorKey: "status", header: "Estado", opFilter: "eq",
      cell: ({cell}) => <StatusBadge status={cell.getValue()}/>,
    },
    {
      accessorKey: "costPrice", header: "Costo",
      cell: ({cell}) => formatters.cash(cell.getValue()),
      exportFormat: row => row.costPrice,
    },
    {
      accessorKey: "salePrice", header: "Precio Venta",
      cell: ({cell}) => formatters.cash(cell.getValue()),
      exportFormat: row => row.salePrice,
    },
    {
      accessorKey: "nuplinState", header: "Estado Nuplin", opFilter: "startsWith",
      cell: ({cell}) => <NuplinStateBadge state={cell.getValue()}/>,
    },
    {
      accessorKey: "nuplinToken", header: "Token",
      cell: ({cell}) => {
        const v = cell.getValue();
        if (!v) return <span className="text-gray-400 text-xs">—</span>;
        return <span className="font-mono text-xs text-gray-600 line-clamp-1" title={v}>{v.length > 20 ? `…${v.slice(-20)}` : v}</span>;
      },
    },
    {
      accessorKey: "activationDate", header: "Fecha Activación",
      cell: ({cell}) => cell.getValue() ? moment(cell.getValue()).format("YYYY-MM-DD HH:mm") : <span className="text-gray-400 text-xs">—</span>,
      exportFormat: row => row.activationDate ? moment(row.activationDate).format("YYYY-MM-DD HH:mm:ss") : "",
    },
    {
      accessorKey: "createdAt", header: "Creado",
      cell: ({cell}) => moment(cell.getValue()).format("YYYY-MM-DD HH:mm"),
      exportFormat: row => moment(row.createdAt).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      id: "actions", header: "Acciones", enableColumnFilter: false,
      cell: ({row}) => {
        const token = row.original;
        const isOpen = openActionMenu?.id === token.id;
        return (
          <div className="relative">
            <button
              onClick={e => {
                const rect = e.currentTarget.getBoundingClientRect();
                setOpenActionMenu(isOpen ? null : {id: token.id, top: rect.bottom + window.scrollY, right: window.innerWidth - rect.right});
              }}
              disabled={loading}
              className="px-3 py-1 bg-blue-800 hover:bg-blue-900 text-white rounded text-xs font-medium flex items-center gap-1"
            >
              Acciones <span className="text-blue-200">{isOpen ? "▲" : "▼"}</span>
            </button>
            {isOpen && (
              <div
                style={{position: "fixed", top: openActionMenu.top, right: openActionMenu.right, zIndex: 9999}}
                className="w-44 bg-white border border-gray-200 rounded shadow-lg"
                onMouseLeave={() => setOpenActionMenu(null)}
              >
                <button onClick={() => { handleEditToken(token); setOpenActionMenu(null); }}
                  className="w-full text-left px-4 py-2 text-xs text-gray-700 hover:bg-gray-50">
                  Editar precios
                </button>
                {token.status === "available" && (
                  <button onClick={() => { handleChangeStatus(token, "used"); setOpenActionMenu(null); }}
                    className="w-full text-left px-4 py-2 text-xs text-green-700 hover:bg-green-50">
                    Marcar Usado
                  </button>
                )}
                {token.status !== "expired" && (
                  <button onClick={() => { handleChangeStatus(token, "expired"); setOpenActionMenu(null); }}
                    className="w-full text-left px-4 py-2 text-xs text-red-600 hover:bg-red-50">
                    Expirar
                  </button>
                )}
                {token.status === "used" && (
                  <button onClick={() => { openNuplinModal("deactivate", token); setOpenActionMenu(null); }}
                    className="w-full text-left px-4 py-2 text-xs text-orange-600 hover:bg-orange-50">
                    Desactivar
                  </button>
                )}
                {token.status === "used" && (
                  <button onClick={() => { openNuplinModal("change-tier", token); setOpenActionMenu(null); }}
                    className="w-full text-left px-4 py-2 text-xs text-purple-600 hover:bg-purple-50">
                    Cambiar Tier
                  </button>
                )}
                {(token.status === "used" || token.status === "expired") && (
                  <button onClick={() => { openNuplinModal("resubscribe", token); setOpenActionMenu(null); }}
                    className="w-full text-left px-4 py-2 text-xs text-teal-600 hover:bg-teal-50">
                    Resuscribir
                  </button>
                )}
                <div className="border-t border-gray-100"/>
                <button onClick={() => { openWebhookEvents(token); setOpenActionMenu(null); }}
                  className="w-full text-left px-4 py-2 text-xs text-gray-500 hover:bg-gray-50">
                  Ver Eventos
                </button>
              </div>
            )}
          </div>
        );
      },
    },
  ], [selectedTokenIds, loading, openActionMenu]);

  const dataFetch = useCallback(async (records, currentPage, order, search, filter) => {
    try {
      const res = await appDataStore.post("/nuplin-tokens/list", {
        records, currentPage,
        order: [["id", "DESC"]],
        search, filter,
        attributes: ["id", "subjectId", "entitlementId", "product", "status", "providerId", "costPrice", "salePrice", "nuplinState", "nuplinToken", "activationDate", "validity", "createdAt"],
        searchAttributes: ["subjectId", "entitlementId", "product", "providerId", "nuplinToken"],
      });
      return {data: res.data || [], totalRows: res.total || 0, totalPages: res.totalPages || 1};
    } catch {
      return {data: [], totalRows: 0, totalPages: 1};
    }
  }, [appDataStore]);

  if (!hasMasterScope) {
    return (
      <div className="p-2 max-w-7xl mx-auto text-black">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Gestión de Tokens Nuplin</h2>
        <div className="bg-red-50 border border-red-300 rounded-lg p-6 max-w-2xl">
          <h3 className="text-lg font-bold text-red-800 mb-2">Acceso Denegado</h3>
          <p className="text-red-700">Se requiere el scope <strong>Master</strong>.</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // Render helpers for bulk modals (shared between deactivate / change-tier)
  // ============================================================
  const PreviewTable = ({rows}) => (
    <table className="w-full text-sm border-collapse">
      <thead className="sticky top-0 bg-gray-100">
        <tr>
          {["Entitlement ID", "Producto", "Estado", "Cliente", "Distribuidor", "Última act.", "Días"].map(h => (
            <th key={h} className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300 text-xs">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className={`border-b border-gray-200 ${!row.found ? "bg-red-50" : "hover:bg-gray-50"}`}>
            <td className="px-3 py-2 font-mono text-xs text-gray-700">{row.entitlementId}</td>
            <td className="px-3 py-2 text-xs text-gray-600">{row.found ? row.product : "—"}</td>
            <td className="px-3 py-2 text-xs">
              {row.found
                ? <span className={`px-2 py-0.5 rounded font-medium text-xs ${row.status === "used" ? "bg-blue-100 text-blue-800" : row.status === "available" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700"}`}>{row.status}</span>
                : <span className="text-red-500 font-medium text-xs">No encontrado</span>
              }
            </td>
            <td className="px-3 py-2 text-xs text-gray-700">{row.found && row.customerName ? row.customerName : "—"}</td>
            <td className="px-3 py-2 text-xs text-gray-700">{row.found && row.distributorName ? row.distributorName : "—"}</td>
            <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">{row.found ? moment(row.updatedAt).format("YYYY-MM-DD HH:mm") : "—"}</td>
            <td className="px-3 py-2 text-center text-xs">
              {row.found ? <span className={`font-bold ${row.daysSinceUpdate >= 30 ? "text-red-600" : row.daysSinceUpdate >= 7 ? "text-yellow-600" : "text-gray-700"}`}>{row.daysSinceUpdate}d</span> : "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const BulkResultSummary = ({results}) => (
    <div className="flex gap-4 mb-4">
      {[
        {label: "Total", val: results.total, cls: "bg-gray-50"},
        {label: "Exitosos", val: results.success?.length || 0, cls: "bg-green-50 text-green-700"},
        {label: "Errores", val: results.errors?.length || 0, cls: "bg-red-50 text-red-700"},
      ].map(({label, val, cls}) => (
        <div key={label} className={`flex-1 rounded-lg p-3 text-center ${cls}`}>
          <p className="text-2xl font-bold">{val}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      ))}
    </div>
  );

  // ============================================================
  return (
    <>
      <div className="p-2 mx-auto text-black">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800"></h2>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => openNuplinModal("initiate-purchase")} disabled={loading}
              className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded font-medium text-sm">
              Generar token
            </button>
            <button onClick={() => setShowBulkChangeTierModal(true)} disabled={loading}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded font-medium text-sm">
              Cambio de Tier Masivo
            </button>
            <button onClick={() => setShowBulkDeactivateModal(true)} disabled={loading}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium text-sm">
              Cancelación Masiva
            </button>
            {selectedTokenIds.length > 0 && (
              <button onClick={() => setShowBulkStatusModal(true)} disabled={loading}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded font-medium text-sm">
                Cambiar Estado ({selectedTokenIds.length})
              </button>
            )}
          </div>
        </div>

        <DataTable
          key={refreshKey}
          columns={columns}
          dataFetch={dataFetch}
          enablePagination
          enableSearch
          enableColumnFilter
          enableExport
          exportFileName="nuplin-tokens"
        />
      </div>

      {/* ─── Edit Modal ─── */}
      {showEditModal && selectedToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Editar Token</h3>
            <div className="space-y-3 mb-4">
              <p className="text-sm text-gray-600">Subject: <span className="font-medium text-gray-800">{selectedToken.subjectId}</span></p>
              <p className="text-sm text-gray-600">Producto: <span className="font-medium text-gray-800">{selectedToken.product}</span></p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Costo</label>
                <input type="number" value={editForm.costPrice} onChange={e => setEditForm(p => ({...p, costPrice: e.target.value}))}
                  placeholder="0.00" step="0.01" className="w-full px-3 py-2 border border-gray-300 rounded text-sm"/>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Precio de Venta</label>
                <input type="number" value={editForm.salePrice} onChange={e => setEditForm(p => ({...p, salePrice: e.target.value}))}
                  placeholder="0.00" step="0.01" className="w-full px-3 py-2 border border-gray-300 rounded text-sm"/>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowEditModal(false)} disabled={loading} className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium">Cancelar</button>
              <button onClick={submitEditToken} disabled={loading} className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded font-medium">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Bulk Status Modal ─── */}
      {showBulkStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Cambiar Estado — {selectedTokenIds.length} tokens</h3>
            <select value={bulkNewStatus} onChange={e => setBulkNewStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm mb-4">
              <option value="">Seleccionar...</option>
              <option value="available">Disponible</option>
              <option value="used">Usado</option>
              <option value="expired">Expirado</option>
              <option value="error">Error</option>
            </select>
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setShowBulkStatusModal(false); setBulkNewStatus(""); }} disabled={loading} className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium">Cancelar</button>
              <button onClick={submitBulkStatusChange} disabled={loading || !bulkNewStatus} className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded font-medium">Cambiar</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Initiate Purchase Modal ─── */}
      {showNuplinModal === "initiate-purchase" && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Generar nuevo Token Nuplin</h3>
            <div className="space-y-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Producto (SKU)</label>
                <select value={initiatePurchaseForm.sku} onChange={e => setInitiatePurchaseForm(p => ({...p, sku: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm">
                  <option value="">Seleccionar...</option>
                  {nuplinProducts.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meses de suscripción</label>
                <input type="number" min="1" max="12" value={initiatePurchaseForm.months}
                  onChange={e => setInitiatePurchaseForm(p => ({...p, months: parseInt(e.target.value)}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"/>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={closeNuplinModal} disabled={loading} className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium">Cancelar</button>
              <button onClick={submitInitiatePurchase} disabled={loading} className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded font-medium">Generar</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Deactivate Modal ─── */}
      {showNuplinModal === "deactivate" && nuplinToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Desactivar Entitlement</h3>
            <div className="p-3 bg-gray-50 rounded text-sm mb-4 space-y-1">
              <p><span className="text-gray-500">Subject ID:</span> <span className="font-medium">{nuplinToken.subjectId}</span></p>
              <p><span className="text-gray-500">Entitlement ID:</span> <span className="font-medium">{nuplinToken.entitlementId}</span></p>
              <p><span className="text-gray-500">Producto:</span> <span className="font-medium">{nuplinToken.product}</span></p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
              <input type="text" value={deactivateReason} onChange={e => setDeactivateReason(e.target.value)}
                placeholder="Ej: User Request" className="w-full px-3 py-2 border border-gray-300 rounded text-sm"/>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={closeNuplinModal} disabled={loading} className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium">Cancelar</button>
              <button onClick={submitDeactivate} disabled={loading} className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded font-medium">Desactivar</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Change Tier Modal ─── */}
      {showNuplinModal === "change-tier" && nuplinToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Cambiar Tier</h3>
            <div className="p-3 bg-gray-50 rounded text-sm mb-4 space-y-1">
              <p><span className="text-gray-500">Subject ID:</span> <span className="font-medium">{nuplinToken.subjectId}</span></p>
              <p><span className="text-gray-500">Producto actual:</span> <span className="font-medium">{nuplinToken.product}</span></p>
            </div>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Producto destino</label>
                <select value={changeTierForm.product} onChange={e => setChangeTierForm(p => ({...p, product: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm">
                  <option value="">Seleccionar...</option>
                  {nuplinProducts.filter(p => p.value !== nuplinToken.product).map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Política</label>
                <select value={changeTierForm.policy} onChange={e => setChangeTierForm(p => ({...p, policy: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm">
                  <option value="IMMEDIATE">Inmediato</option>
                  <option value="">Diferido (fin de ciclo)</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={closeNuplinModal} disabled={loading} className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium">Cancelar</button>
              <button onClick={submitChangeTier} disabled={loading} className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded font-medium">Cambiar Tier</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Resubscribe Modal ─── */}
      {showNuplinModal === "resubscribe" && nuplinToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Resuscribir Entitlement</h3>
            <div className="p-3 bg-gray-50 rounded text-sm mb-4 space-y-1">
              <p><span className="text-gray-500">Subject ID:</span> <span className="font-medium">{nuplinToken.subjectId}</span></p>
              <p><span className="text-gray-500">Entitlement ID:</span> <span className="font-medium">{nuplinToken.entitlementId}</span></p>
              <p><span className="text-gray-500">Producto:</span> <span className="font-medium">{nuplinToken.product}</span></p>
              <p><span className="text-gray-500">Vigencia:</span> <span className="font-medium">{nuplinToken.validity} mes(es)</span></p>
            </div>
            <p className="text-sm text-gray-600 mb-4">Se creará una nueva instancia de suscripción.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={closeNuplinModal} disabled={loading} className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium">Cancelar</button>
              <button onClick={submitResubscribe} disabled={loading} className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded font-medium">Resuscribir</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Webhook Events Modal ─── */}
      {webhookEventsToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-[90vw] max-h-[98vh] flex flex-col shadow-lg">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-800">Eventos Nuplin</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Subject: <span className="font-medium text-gray-700">{webhookEventsToken.subjectId}</span>
                  {" · "}
                  Entitlement: <span className="font-medium text-gray-700">{webhookEventsToken.entitlementId}</span>
                </p>
              </div>
              <button onClick={() => { setWebhookEventsToken(null); setExpandedPayload(null); }}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none">×</button>
            </div>
            <div className="overflow-y-auto flex-1">
              {loadingWebhookEvents ? (
                <div className="text-center py-8 text-gray-500">Cargando eventos...</div>
              ) : webhookEvents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No hay eventos registrados.</div>
              ) : (
                <table className="w-full text-sm border-collapse">
                  <thead className="sticky top-0 bg-gray-100">
                    <tr>
                      {["Fecha", "Código", "Tipo", "Proveedor", "Mensaje", "Payload"].map(h => (
                        <th key={h} className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {webhookEvents.map(event => (
                      <>
                        <tr key={event.id} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{moment(event.receivedAt).format("YYYY-MM-DD HH:mm:ss")}</td>
                          <td className="px-3 py-2 font-mono text-xs text-gray-800">{event.code}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">{event.eventType || "—"}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">{event.provider || "—"}</td>
                          <td className="px-3 py-2 text-gray-700 max-w-xs truncate">{event.message || "—"}</td>
                          <td className="px-3 py-2">
                            <button onClick={() => setExpandedPayload(expandedPayload === event.id ? null : event.id)}
                              className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-xs">
                              {expandedPayload === event.id ? "Cerrar" : "Ver"}
                            </button>
                          </td>
                        </tr>
                        {expandedPayload === event.id && (
                          <tr key={`${event.id}-payload`} className="bg-gray-50">
                            <td colSpan={6} className="px-3 py-2">
                              <pre className="text-xs text-gray-700 bg-gray-100 rounded p-3 overflow-x-auto whitespace-pre-wrap break-all">
                                {JSON.stringify(event.rawPayload, null, 2)}
                              </pre>
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Bulk Deactivate Modal ─── */}
      {showBulkDeactivateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-[96vw] max-h-[96vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Cancelación Masiva</h3>
              <button onClick={() => setShowBulkDeactivateModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl font-bold">×</button>
            </div>

            {!bulkDeactivatePreview && !bulkDeactivateResults && (
              <>
                <textarea value={bulkDeactivateInput} onChange={e => setBulkDeactivateInput(e.target.value)}
                  placeholder={"novaENT_1001\nnovaENT_1002"} rows={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono mb-2 flex-1"/>
                <p className="text-xs text-gray-500 mb-4">{parsedEntitlementIds.length} entitlements ingresados</p>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setShowBulkDeactivateModal(false)} disabled={loading} className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium">Cancelar</button>
                  <button onClick={fetchBulkDeactivatePreview} disabled={loading || !parsedEntitlementIds.length} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">{loading ? "Consultando..." : "Ver Reporte"}</button>
                </div>
              </>
            )}

            {bulkDeactivatePreview && !bulkDeactivateResults && (
              <>
                <div className="flex gap-3 mb-3 text-sm">
                  <span className="bg-gray-100 px-2 py-1 rounded">Total: <strong>{bulkDeactivatePreview.length}</strong></span>
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded">Encontrados: <strong>{bulkDeactivatePreview.filter(r => r.found).length}</strong></span>
                  <span className="bg-red-100 text-red-700 px-2 py-1 rounded">No encontrados: <strong>{bulkDeactivatePreview.filter(r => !r.found).length}</strong></span>
                </div>
                <div className="flex-1 overflow-y-auto mb-4"><PreviewTable rows={bulkDeactivatePreview}/></div>
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Motivo de cancelación</label>
                  <input type="text" value={bulkDeactivateReason} onChange={e => setBulkDeactivateReason(e.target.value)}
                    placeholder="Ej: User Request" className="w-full px-3 py-2 border border-gray-300 rounded text-sm mb-3"/>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setBulkDeactivatePreview(null)} disabled={loading} className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium">Volver</button>
                    <button onClick={submitBulkDeactivate} disabled={loading || !bulkDeactivateReason} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium">{loading ? "Procesando..." : `Cancelar ${bulkDeactivatePreview.filter(r => r.found).length} entitlements`}</button>
                  </div>
                </div>
              </>
            )}

            {bulkDeactivateResults && (
              <>
                <div className="flex-1 overflow-y-auto"><BulkResultSummary results={bulkDeactivateResults}/></div>
                <div className="flex gap-2 justify-end mt-4">
                  <button onClick={() => { setShowBulkDeactivateModal(false); setRefreshKey(k => k + 1); }} className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium">Cerrar</button>
                  <button onClick={() => { setBulkDeactivateResults(null); setBulkDeactivateInput(""); }} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium">Nueva Cancelación</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ─── Bulk Change Tier Modal ─── */}
      {showBulkChangeTierModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-[96vw] max-h-[96vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Cambio de Tier Masivo</h3>
              <button onClick={() => setShowBulkChangeTierModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl font-bold">×</button>
            </div>

            {!bulkChangeTierPreview && !bulkChangeTierResults && (
              <>
                <textarea value={bulkChangeTierInput} onChange={e => setBulkChangeTierInput(e.target.value)}
                  placeholder={"novaENT_1001\nnovaENT_1002"} rows={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono mb-2 flex-1"/>
                <p className="text-xs text-gray-500 mb-4">{parsedChangeTierIds.length} entitlements ingresados</p>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setShowBulkChangeTierModal(false)} disabled={loading} className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium">Cancelar</button>
                  <button onClick={fetchBulkChangeTierPreview} disabled={loading || !parsedChangeTierIds.length} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">{loading ? "Consultando..." : "Ver Reporte"}</button>
                </div>
              </>
            )}

            {bulkChangeTierPreview && !bulkChangeTierResults && (
              <>
                <div className="flex gap-3 mb-3 text-sm">
                  <span className="bg-gray-100 px-2 py-1 rounded">Total: <strong>{bulkChangeTierPreview.length}</strong></span>
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded">Encontrados: <strong>{bulkChangeTierPreview.filter(r => r.found).length}</strong></span>
                  <span className="bg-red-100 text-red-700 px-2 py-1 rounded">No encontrados: <strong>{bulkChangeTierPreview.filter(r => !r.found).length}</strong></span>
                </div>
                <div className="flex-1 overflow-y-auto mb-4"><PreviewTable rows={bulkChangeTierPreview}/></div>
                <div className="border-t pt-4">
                  <div className="flex gap-4 mb-3">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Producto destino</label>
                      <select value={bulkChangeTierForm.toProduct} onChange={e => setBulkChangeTierForm(p => ({...p, toProduct: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm">
                        <option value="">Seleccionar...</option>
                        {nuplinProducts.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Política</label>
                      <select value={bulkChangeTierForm.policy} onChange={e => setBulkChangeTierForm(p => ({...p, policy: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm">
                        <option value="IMMEDIATE">Inmediato</option>
                        <option value="">Diferido (fin de ciclo)</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setBulkChangeTierPreview(null)} disabled={loading} className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium">Volver</button>
                    <button onClick={submitBulkChangeTier} disabled={loading || !bulkChangeTierForm.toProduct} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded font-medium">{loading ? "Procesando..." : `Cambiar tier (${bulkChangeTierPreview.filter(r => r.found).length})`}</button>
                  </div>
                </div>
              </>
            )}

            {bulkChangeTierResults && (
              <>
                <div className="flex-1 overflow-y-auto"><BulkResultSummary results={bulkChangeTierResults}/></div>
                <div className="flex gap-2 justify-end mt-4">
                  <button onClick={() => { setShowBulkChangeTierModal(false); setRefreshKey(k => k + 1); }} className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded font-medium">Cerrar</button>
                  <button onClick={() => { setBulkChangeTierResults(null); setBulkChangeTierInput(""); }} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded font-medium">Nuevo Cambio</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
});